import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config';

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Types
export interface EmergencyContact {
  id?: string;
  staff_id?: string;
  name: string;
  relationship: string;
  phone: string;
  email?: string;
  address?: string;
  is_primary?: boolean;
}

export interface StaffMember {
  id?: string;
  user_id?: string;
  organization_id?: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  position: string;
  department?: string;
  start_date: string;
  end_date?: string;
  employment_type: 'Full-time' | 'Part-time' | 'Contract' | 'Intern';
  weekly_hours?: number;
  hourly_rate?: number;
  annual_salary?: number;
  photo_url?: string;
  status?: 'active' | 'on_leave' | 'suspended' | 'terminated';
  emergency_contacts?: EmergencyContact[];
  work_shifts?: WorkShift[];
  holidays?: Holiday[];
  created_at?: string;
  updated_at?: string;
}

export interface WorkShift {
  id?: string;
  staff_id: string;
  organization_id?: string | null;
  day_of_week: number; // 0=Sunday, 1=Monday, etc.
  start_time: string;
  end_time: string;
  role?: string;
  location?: string;
  notes?: string;
  is_recurring?: boolean;
}

export interface Holiday {
  id?: string;
  staff_id: string;
  organization_id?: string | null;
  start_date: string;
  end_date: string;
  type: 'Annual Leave' | 'Sick Leave' | 'Unpaid Leave' | 'Public Holiday' | 'Compassionate Leave' | 'Maternity Leave' | 'Paternity Leave';
  status: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
  days_count?: number;
  notes?: string;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
}

export interface HolidayAllowance {
  id?: string;
  staff_id: string;
  year: number;
  total_days: number;
  used_days: number;
  remaining_days: number;
  carried_over_days?: number;
}

const staffManagementService = {
  // ==================== STAFF MEMBERS ====================
  
  async listStaff(organizationId?: string): Promise<{ data: StaffMember[]; count: number }> {
    try {
      const headers = await getAuthHeaders();
      const url = organizationId 
        ? `${API_BASE_URL}/api/staff/members?organization_id=${organizationId}`
        : `${API_BASE_URL}/api/staff/members`;
      
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to fetch staff members');
      return response.json();
    } catch (error: any) {
      console.error('Error fetching staff:', error);
      throw error;
    }
  },

  async getStaff(id: string): Promise<{ data: StaffMember }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/members/${id}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch staff member');
      return response.json();
    } catch (error: any) {
      console.error('Error fetching staff member:', error);
      throw error;
    }
  },

  async createStaff(staff: Partial<StaffMember>): Promise<{ data: StaffMember }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/members`, {
        method: 'POST',
        headers,
        body: JSON.stringify(staff)
      });
      if (!response.ok) throw new Error('Failed to create staff member');
      return response.json();
    } catch (error: any) {
      console.error('Error creating staff member:', error);
      throw error;
    }
  },

  async updateStaff(id: string, updates: Partial<StaffMember>): Promise<{ data: StaffMember }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/members/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update staff member');
      return response.json();
    } catch (error: any) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  },

  async deleteStaff(id: string): Promise<{ success: boolean }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/members/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) throw new Error('Failed to delete staff member');
      return response.json();
    } catch (error: any) {
      console.error('Error deleting staff member:', error);
      throw error;
    }
  },

  // ==================== EMERGENCY CONTACTS ====================

  async addEmergencyContact(contact: EmergencyContact): Promise<{ data: EmergencyContact }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/emergency-contacts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(contact)
      });
      if (!response.ok) throw new Error('Failed to add emergency contact');
      return response.json();
    } catch (error: any) {
      console.error('Error adding emergency contact:', error);
      throw error;
    }
  },

  async updateEmergencyContact(id: string, updates: Partial<EmergencyContact>): Promise<{ data: EmergencyContact }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/emergency-contacts/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update emergency contact');
      return response.json();
    } catch (error: any) {
      console.error('Error updating emergency contact:', error);
      throw error;
    }
  },

  async deleteEmergencyContact(id: string): Promise<{ success: boolean }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/emergency-contacts/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) throw new Error('Failed to delete emergency contact');
      return response.json();
    } catch (error: any) {
      console.error('Error deleting emergency contact:', error);
      throw error;
    }
  },

  // ==================== WORK SHIFTS ====================

  async listShifts(organizationId?: string, staffId?: string): Promise<{ data: WorkShift[] }> {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (organizationId) params.append('organization_id', organizationId);
      if (staffId) params.append('staff_id', staffId);
      
      const response = await fetch(`${API_BASE_URL}/api/staff/shifts?${params}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch shifts');
      return response.json();
    } catch (error: any) {
      console.error('Error fetching shifts:', error);
      throw error;
    }
  },

  async createShift(shift: Partial<WorkShift>): Promise<{ data: WorkShift }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/shifts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(shift)
      });
      if (!response.ok) throw new Error('Failed to create shift');
      return response.json();
    } catch (error: any) {
      console.error('Error creating shift:', error);
      throw error;
    }
  },

  async updateShift(id: string, updates: Partial<WorkShift>): Promise<{ data: WorkShift }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/shifts/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update shift');
      return response.json();
    } catch (error: any) {
      console.error('Error updating shift:', error);
      throw error;
    }
  },

  async deleteShift(id: string): Promise<{ success: boolean }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/shifts/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) throw new Error('Failed to delete shift');
      return response.json();
    } catch (error: any) {
      console.error('Error deleting shift:', error);
      throw error;
    }
  },

  // ==================== HOLIDAYS ====================

  async listHolidays(organizationId?: string, staffId?: string, status?: string): Promise<{ data: Holiday[] }> {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (organizationId) params.append('organization_id', organizationId);
      if (staffId) params.append('staff_id', staffId);
      if (status) params.append('status', status);
      
      const response = await fetch(`${API_BASE_URL}/api/staff/holidays?${params}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch holidays');
      return response.json();
    } catch (error: any) {
      console.error('Error fetching holidays:', error);
      throw error;
    }
  },

  async createHoliday(holiday: Partial<Holiday>): Promise<{ data: Holiday }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/holidays`, {
        method: 'POST',
        headers,
        body: JSON.stringify(holiday)
      });
      if (!response.ok) throw new Error('Failed to create holiday request');
      return response.json();
    } catch (error: any) {
      console.error('Error creating holiday:', error);
      throw error;
    }
  },

  async updateHoliday(id: string, updates: Partial<Holiday>): Promise<{ data: Holiday }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/holidays/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update holiday');
      return response.json();
    } catch (error: any) {
      console.error('Error updating holiday:', error);
      throw error;
    }
  },

  async deleteHoliday(id: string): Promise<{ success: boolean }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/staff/holidays/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) throw new Error('Failed to delete holiday');
      return response.json();
    } catch (error: any) {
      console.error('Error deleting holiday:', error);
      throw error;
    }
  },

  // ==================== HOLIDAY ALLOWANCES ====================

  async getHolidayAllowances(staffId?: string, year?: number): Promise<{ data: HolidayAllowance[] }> {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (staffId) params.append('staff_id', staffId);
      if (year) params.append('year', year.toString());
      
      const response = await fetch(`${API_BASE_URL}/api/staff/holiday-allowances?${params}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch holiday allowances');
      return response.json();
    } catch (error: any) {
      console.error('Error fetching holiday allowances:', error);
      throw error;
    }
  },

  // ==================== COVERAGE ====================

  async getCoverage(organizationId?: string): Promise<any> {
    try {
      const headers = await getAuthHeaders();
      const url = organizationId 
        ? `${API_BASE_URL}/api/staff/coverage?organization_id=${organizationId}`
        : `${API_BASE_URL}/api/staff/coverage`;
      
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to fetch coverage data');
      return response.json();
    } catch (error: any) {
      console.error('Error fetching coverage:', error);
      throw error;
    }
  },

  // ==================== PAYROLL ====================

  async getPayrollSummary(month: string, payPeriod: 'weekly' | 'monthly'): Promise<{ data: any[]; error?: any }> {
    try {
      // Query the staff_payroll_summary view directly from Supabase
      const { data, error } = await supabase
        .from('staff_payroll_summary')
        .select('*');
      
      if (error) {
        console.error('Error fetching payroll summary:', error);
        return { data: [], error };
      }
      
      return { data: data || [] };
    } catch (error: any) {
      console.error('Error fetching payroll summary:', error);
      return { data: [], error };
    }
  },

  async createPayrollPeriod(period: {
    period_start: string;
    period_end: string;
    payment_date?: string;
    organization_id?: string;
  }): Promise<{ data: any; error?: any }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('staff_payroll_periods')
        .insert({
          ...period,
          user_id: user.id,
          status: 'open'
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error creating payroll period:', error);
        return { data: null, error };
      }
      
      return { data };
    } catch (error: any) {
      console.error('Error creating payroll period:', error);
      return { data: null, error };
    }
  },

  async getPayrollPeriods(organizationId?: string): Promise<{ data: any[]; error?: any }> {
    try {
      let query = supabase
        .from('staff_payroll_periods')
        .select('*')
        .order('period_start', { ascending: false });
      
      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching payroll periods:', error);
        return { data: [], error };
      }
      
      return { data: data || [] };
    } catch (error: any) {
      console.error('Error fetching payroll periods:', error);
      return { data: [], error };
    }
  },

  async generatePayrollEntries(periodId: string): Promise<{ data: any[]; error?: any }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Call the stored procedure to generate payroll entries
      const { data, error } = await supabase
        .rpc('generate_payroll_for_period', {
          p_period_id: periodId,
          p_user_id: user.id
        });
      
      if (error) {
        console.error('Error generating payroll entries:', error);
        return { data: [], error };
      }
      
      return { data: data || [] };
    } catch (error: any) {
      console.error('Error generating payroll entries:', error);
      return { data: [], error };
    }
  },

  async getPayrollEntries(periodId: string): Promise<{ data: any[]; error?: any }> {
    try {
      const { data, error } = await supabase
        .from('staff_payroll_entries')
        .select(`
          *,
          staff:staff_members(first_name, last_name, email, position, department)
        `)
        .eq('payroll_period_id', periodId);
      
      if (error) {
        console.error('Error fetching payroll entries:', error);
        return { data: [], error };
      }
      
      return { data: data || [] };
    } catch (error: any) {
      console.error('Error fetching payroll entries:', error);
      return { data: [], error };
    }
  },

  async approvePayrollPeriod(periodId: string): Promise<{ data: any; error?: any }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('approve_payroll_period', {
          p_period_id: periodId,
          p_user_id: user.id
        });
      
      if (error) {
        console.error('Error approving payroll period:', error);
        return { data: null, error };
      }
      
      return { data };
    } catch (error: any) {
      console.error('Error approving payroll period:', error);
      return { data: null, error };
    }
  },

  async reopenPayrollPeriod(periodId: string): Promise<{ data: any; error?: any }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('reopen_payroll_period', {
          p_period_id: periodId,
          p_user_id: user.id
        });
      
      if (error) {
        console.error('Error reopening payroll period:', error);
        return { data: null, error };
      }
      
      return { data };
    } catch (error: any) {
      console.error('Error reopening payroll period:', error);
      return { data: null, error };
    }
  },

  async updatePayrollPeriodStatus(
    periodId: string, 
    status: 'open' | 'processing' | 'approved' | 'paid' | 'closed'
  ): Promise<{ data: any; error?: any }> {
    try {
      const { data, error } = await supabase
        .from('staff_payroll_periods')
        .update({ status })
        .eq('id', periodId)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating payroll period status:', error);
        return { data: null, error };
      }
      
      return { data };
    } catch (error: any) {
      console.error('Error updating payroll period status:', error);
      return { data: null, error };
    }
  },

  async deletePayrollPeriod(periodId: string): Promise<{ success: boolean; error?: any }> {
    try {
      const { error } = await supabase
        .from('staff_payroll_periods')
        .delete()
        .eq('id', periodId);
      
      if (error) {
        console.error('Error deleting payroll period:', error);
        return { success: false, error };
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting payroll period:', error);
      return { success: false, error };
    }
  }
};

export default staffManagementService;
